import { Paper, Typography, IconButton, Tooltip, Alert, Box } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { useQuery } from '@apollo/client';
import {
  ActivationStatus,
  GetDirectoriesExtendedInfo,
  GetOrganizationAdministrators,
  ReadonlyProperty
} from 'Apollo';
import { DataRow } from 'Theme/components/DataRow';
import Skeleton from 'Theme/components/Skeleton';
import PaperTitle from 'Theme/components/PaperTitle';
import EditNameToggle from './EditName';
import EditPhoneToggle from './EditPhone';
import EditLanguageToggle from '../Account/EditLanguage';
import EmailRow from './EmailRow/EmailRow';
import OrganizationOwnersInfo from './OrganizationOwnersInfo';
import TestIds from 'Tests/TestIds';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { userFullName } from 'Tools';
import { FormattedDate } from 'react-intl';

interface Props {
  person: IPersonWithAccount;
  canManageProfile: boolean;
  isCurrentUser: boolean;
  isOrganizationAdmin: boolean;
  organizationCode?: string;
}

function Account(props: Readonly<Props>) {
  const { person, canManageProfile, isCurrentUser, isOrganizationAdmin, organizationCode } = props;

  const ts = useTranslate();
  const { onError } = useNotification();

  const { data, loading } = useQuery<
    GetDirectoriesExtendedInfoData,
    GetDirectoriesExtendedInfoVars
  >(GetDirectoriesExtendedInfo, {
    variables: {
      personImmutableId: person.immutableId
    },
    onError,
    fetchPolicy: 'cache-first'
  });

  const showsOrganizationManagedInfo =
    isCurrentUser && organizationCode != null && !isOrganizationAdmin;

  const { data: organizationAdministratorsData } = useQuery<
    GetOrganizationAdministratorsData,
    GetOrganizationAdministratorsVars
  >(GetOrganizationAdministrators, {
    variables: {
      organizationCode: organizationCode!
    },
    skip: !showsOrganizationManagedInfo,
    onError,
    fetchPolicy: 'cache-first'
  });

  const organizationOwners =
    organizationAdministratorsData?.organizationAdministrators ?? [];

  const redirectUrl = encodeURIComponent('/');

  const authDirs = data === undefined ? [] : data.directoriesAuthenticatingPerson;
  const hasAFederatedDirectory = authDirs.some(d => d.isFederated);

  const readonlyPropertiesTabs: ReadonlyProperty[] = [];

  authDirs.forEach(d => {
    if (d.profileManagement?.readonlyProperties.length > 0) {
      for (let i = 0; i < d.profileManagement?.readonlyProperties.length; i++) {
        readonlyPropertiesTabs.push(d.profileManagement?.readonlyProperties[i]);
      }
    }
  });

  const isDisabled = person?.status === ActivationStatus.Disabled;
  const isEditable = canManageProfile && !isDisabled;
  const canEditEmail =
    !isDisabled &&
    (isOrganizationAdmin ||
    (isCurrentUser && organizationCode == null && !hasAFederatedDirectory));

  const canEditPhone =
    (!readonlyPropertiesTabs.includes(ReadonlyProperty.PhoneNumber) ||
      !readonlyPropertiesTabs.includes(ReadonlyProperty.MobileNumber)) &&
    isEditable;

  const canEditName =
    (!readonlyPropertiesTabs.includes(ReadonlyProperty.LastName) ||
      !readonlyPropertiesTabs.includes(ReadonlyProperty.FirstName)) &&
    isEditable;

  const canEditLanguage =
    !readonlyPropertiesTabs.includes(ReadonlyProperty.LanguageCode) &&
    (isEditable || isCurrentUser);

  return (
    <Paper style={{ marginBottom: '16px' }}>
      <PaperTitle title={ts('page.userProfile.section.account.title')} />
      {showsOrganizationManagedInfo && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Alert severity="info" sx={{ width: '90%' }}>
            <OrganizationOwnersInfo owners={organizationOwners} />
          </Alert>
        </Box>
      )}
      {loading && (
        <>
          {Array.from({ length: 4 }).map((_, index) => (
            <DataRow
              key={index as any}
              label={<Skeleton variant="rectangular" width="100px" />}
              divider={index < 3}
            >
              <Skeleton variant="rectangular" width="200px" />
            </DataRow>
          ))}
        </>
      )}
      {!loading && (
        <>
          <DataRow
            label={ts('page.userProfile.section.personal.fullname')}
            action={
              canEditName && (
                <EditNameToggle
                  person={props.person}
                  readonlyProperties={readonlyPropertiesTabs}
                />
              )
            }
            divider={true}
          >
            <Typography
              variant="body2"
              data-testid={TestIds.pages.userProfile.account.fullnameValue}
            >
              {userFullName(props.person.firstName, props.person.lastName)}
            </Typography>
          </DataRow>
          {!hasAFederatedDirectory && (
            <DataRow
              label={ts('page.userProfile.section.personal.password')}
              divider={true}
              action={
                isCurrentUser &&
                !isDisabled && (
                  <Tooltip
                    title={ts('page.userProfile.editPassword')}
                    placement="bottom"
                  >
                    <IconButton
                      href={
                        `${AppSettings.API_ENDPOINT_URI}` +
                        `pmsession/login?logintype=changepassword&token=${encodeURIComponent(
                          props.person.email!
                        )}&returnUrl=${encodeURIComponent(
                          `/end_login?returnUrl=${redirectUrl}`
                        )}`
                      }
                      size="large"
                    >
                      <CreateIcon />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <Typography variant="body2">•••••••••</Typography>
            </DataRow>
          )}

          <EmailRow
            person={props.person}
            canEditEmail={canEditEmail}
            isOrganizationAdmin={isOrganizationAdmin}
            isCurrentUser={isCurrentUser}
            organizationCode={organizationCode}
          />

          <DataRow
            label={ts('page.userProfile.section.personal.phone')}
            action={
              canEditPhone && (
                <EditPhoneToggle
                  person={props.person}
                  readonlyProperties={readonlyPropertiesTabs}
                />
              )
            }
            divider={true}
          >
            <div>
              {props.person.phoneNumber && (
                <Typography
                  variant="body2"
                  data-testid={TestIds.pages.userProfile.account.phoneValue}
                >
                  {props.person.phoneNumber}
                </Typography>
              )}
              {props.person.mobileNumber && (
                <Typography
                  variant="body2"
                  data-testid={
                    TestIds.pages.userProfile.account.mobilePhoneValue
                  }
                >
                  {props.person.mobileNumber}
                </Typography>
              )}
            </div>
          </DataRow>
          <DataRow
            label={ts('profile.about.language')}
            action={
              canEditLanguage && <EditLanguageToggle person={props.person} />
            }
            divider={true}
          >
            <Typography
              variant="body2"
              data-testid={TestIds.pages.userProfile.account.languageValue}
            >
              {props.person.language ? props.person.language.label : '/'}
            </Typography>
          </DataRow>
          <DataRow label={ts('profile.about.creationDate')} divider={true}>
            <Typography
              variant="body2"
              data-testid={TestIds.pages.userProfile.account.creationValue}
            >
              <FormattedDate value={props.person.creationDate} />
            </Typography>
          </DataRow>
        </>
      )}
    </Paper>
  );
}

export default Account;
